import React, { useEffect, useState } from "react";
import { ForgeNavBar } from "../universe/Universe";
import "../providers/providers.css";
import AriaHologram from "../../components/AriaHologram";

const API_BASE = "" || "";

function useKernelToken() {
  const [token, setToken] = useState(() => localStorage.getItem("forge:kernel_token") || "");
  const save = (t) => { localStorage.setItem("forge:kernel_token", t); setToken(t); };
  return [token, save];
}

async function apiGet(path, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`GET ${path} → ${r.status}`);
  return r.json();
}
async function apiJson(path, method, body, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `${method} ${path} → ${r.status}`);
  return data;
}

export default function ForgeAccessPage() {
  const [token, saveToken] = useKernelToken();
  const [cfg, setCfg] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [allowIps, setAllowIps] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await apiGet("/api/kernel/forge-access", token);
      const c = r.config || {};
      setCfg(c);
      setPublicUrl(c.public_url || "");
      setEnabled(c.enabled !== false);
      setAllowIps((c.allow_ips || []).join("\n"));
      setNotes(c.notes || "");
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    }
  };
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await apiJson("/api/kernel/forge-access", "PATCH", {
        public_url: publicUrl || null,
        enabled,
        allow_ips: allowIps.split("\n").map(s => s.trim()).filter(Boolean),
        notes,
      }, token);
      setMsg({ kind: "ok", text: "Configurazione salvata" });
      load();
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fp-page">
      <ForgeNavBar current="/superadmin/forge-access" />
      <div className="fp-container">
        <header className="fp-header">
          <div>
            <h1>🔐 Forge Access</h1>
            <p>Configura il link pubblico di Forge e chi può accedervi. Nessuna modifica manuale al .env.</p>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <AriaHologram size={140} />
          </div>
        </header>

        {!token && (
          <div className="fp-token-gate">
            <h2>🔐 FORGE Kernel Token</h2>
            <p>Serve il token per accedere a questa configurazione.</p>
            <div className="row">
              <input type="password" placeholder="forge-founder-…"
                onChange={(e) => e.target.value.length > 20 && saveToken(e.target.value)}
                data-testid="fa-token-input"/>
            </div>
          </div>
        )}

        {token && (
          <section className="fp-section">
            <div className="fp-section-title"><span>Configurazione</span></div>
            <div style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(148,163,184,0.12)",
              borderRadius: 12, padding: 20, display: "flex",
              flexDirection: "column", gap: 14,
            }}>
              <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8" }}>Public URL</label>
              <input
                type="text"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                placeholder="https://forge.esempio.com"
                data-testid="fa-public-url"
                style={{ padding: "10px 12px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, color: "#fff", fontSize: 13, outline: "none" }}
              />

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <label style={{ fontSize: 12, color: "#cbd5e1" }}>Forge accessibile:</label>
                <button
                  type="button"
                  onClick={() => setEnabled(!enabled)}
                  data-testid="fa-toggle"
                  className={`fp-btn toggle ${enabled ? "on" : "off"}`}
                >{enabled ? "ATTIVO" : "SPENTO"}</button>
              </div>

              <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8" }}>Allow IPs (uno per riga · vuoto = tutti)</label>
              <textarea
                rows={3}
                value={allowIps}
                onChange={(e) => setAllowIps(e.target.value)}
                placeholder="192.168.1.1&#10;10.0.0.5"
                data-testid="fa-allow-ips"
                style={{ padding: "10px 12px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, color: "#fff", fontSize: 12, fontFamily: "SF Mono, monospace", outline: "none" }}
              />

              <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8" }}>Note</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="fa-notes"
                style={{ padding: "10px 12px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, color: "#fff", fontSize: 12, outline: "none" }}
              />

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
                <button className="fp-btn primary" onClick={save} disabled={saving} data-testid="fa-save">
                  {saving ? "Salvo…" : "Salva"}
                </button>
              </div>

              {publicUrl && enabled && (
                <div style={{ marginTop: 10, padding: 12, background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 8 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#67e8f9", marginBottom: 6 }}>Preview link</div>
                  <a href={publicUrl} target="_blank" rel="noreferrer" style={{ color: "#a5f3fc", fontFamily: "SF Mono, monospace", fontSize: 12 }} data-testid="fa-preview-link">{publicUrl}</a>
                </div>
              )}

              {msg && (
                <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: msg.kind === "ok" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: msg.kind === "ok" ? "#bbf7d0" : "#fecaca", fontSize: 12 }}>
                  {msg.text}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
